// What the image viewer steps through: the images around the one pressed.
//
// Promise:
// - A gallery is the outermost element marked `data-media-gallery` around the
//   pressed one: a message (its text's images and its attached ones), a body
//   drawn statically (an ask's, a Case's), a document's editor (its image
//   blocks).
// - Its images are its image buttons (`button[data-file-open]`, a body's and
//   a document's) and the elements marked `data-media-item` (a message's
//   thumbnails), each holding its `<img>`, in the page's order. The same
//   image twice counts once (the first).
// - Only an image pressed as one of these, or a mark in the text pointing at
//   one (`data-media-ref`: "image 2"), steps; anything else opens alone (a ui
//   block's image, a file not sent yet, the app's box).
// - Who put the images there and when are read from the nearest element
//   around each one that says (`data-media-author`, `data-media-at`).
// - Read once, when the viewer opens: images added later are not in it.
import type { FileLightboxDetail } from "./file-lightbox";

export const MEDIA_GALLERY_ATTR = "data-media-gallery";
export const MEDIA_ITEM_ATTR = "data-media-item";
export const MEDIA_REF_ATTR = "data-media-ref";
export const MEDIA_AUTHOR_ATTR = "data-media-author";
export const MEDIA_AT_ATTR = "data-media-at";

const ITEM = `button[data-file-open], [${MEDIA_ITEM_ATTR}]`;

/** One image to step through, and the element it is drawn as (for focus and its place on the screen). */
export interface GalleryItem extends FileLightboxDetail {
  el: Element | null;
}

export interface Gallery {
  items: GalleryItem[];
  index: number;
}

/** The outermost gallery around `el`, or null. */
function galleryOf(el: Element): Element | null {
  let found: Element | null = null;
  for (
    let at = el.closest(`[${MEDIA_GALLERY_ATTR}]`);
    at;
    at = at.parentElement?.closest(`[${MEDIA_GALLERY_ATTR}]`) ?? null
  )
    found = at;
  return found;
}

function readItem(el: Element): GalleryItem | null {
  const img = el instanceof HTMLImageElement ? el : el.querySelector("img");
  // The attribute, as callers pass it (`img.src` is absolute).
  const src = img?.getAttribute("src");
  if (!img || !src) return null;
  const author = el.closest(`[${MEDIA_AUTHOR_ATTR}]`);
  const at = el.closest(`[${MEDIA_AT_ATTR}]`);
  return {
    src,
    name: img.getAttribute("alt") ?? "",
    download: `${src}?dl=1`,
    author: author?.getAttribute(MEDIA_AUTHOR_ATTR) ?? undefined,
    at: at?.getAttribute(MEDIA_AT_ATTR) ?? undefined,
    el,
  };
}

/** The images to step through from `from`, pressed to open `detail`. */
export function gatherGallery(
  from: Element | null,
  detail: FileLightboxDetail,
): Gallery {
  const alone: Gallery = { items: [{ ...detail, el: from }], index: 0 };
  if (!from?.matches(`${ITEM}, [${MEDIA_REF_ATTR}]`)) return alone;
  const root = galleryOf(from);
  if (!root) return alone;
  const items: GalleryItem[] = [];
  const seen = new Set<string>();
  for (const el of root.querySelectorAll(ITEM)) {
    const item = readItem(el);
    if (!item || seen.has(item.src)) continue;
    seen.add(item.src);
    items.push(item);
  }
  const index = items.findIndex((i) => i.src === detail.src);
  if (index < 0) return alone;
  // The pressed one as its caller describes it (its download, its name).
  const pressed = items[index] as GalleryItem;
  items[index] = { ...pressed, ...detail, el: pressed.el };
  return { items, index };
}
